import React, { useState, useContext, useMemo, Suspense, lazy } from "react";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { getNameInitials } from "../constants/globalFunctions";
import IconComponent from "../icons";
import { ThemeContext } from "../../ProviderApp";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import UserProfileModal from "../../components/UserProfileModalComponent";
import { openExternalUser } from "../../reducers/userProfileModal";
import AvatarComponent, { AvatarSize } from "./AvatarComponent";
import UserTagDataFetcher from "./UserTagDataFetcher";
import get from "lodash/get";
import { RootState } from "@Reducers/index";
import { Box } from "@mui/material";
import { TranslatedString } from "@i18n";
import { objectIdString } from "@Helpers/schemas/common.schema";

/**
 * Lazy on purpose. This user tag renders on nearly every screen, so a static import here put the
 * grouped-users modal — and everything it reaches (SlickGrid formatters -> reviews -> portal
 * settings -> estimation -> the HOOPS viewer) — into the entry chunk. The modal only mounts on a
 * grouped-avatar click, so it belongs in its own chunk.
 */
const GroupedUsersDetailsModal = lazy(() => import("@Components/UserProfileModalComponent/GroupedUsersDetailsModal"));

/**
 * `size` is a loose `string` on this shared tag's props (~80 call sites, many of them untyped grid
 * rows), so it is checked against the closed `AvatarSize` union rather than asserted into it. An
 * unrecognised size becomes `undefined`, which is what `AvatarComponent` already falls back on.
 */
const AVATAR_SIZES = ["XXS", "XS", "LSM", "SM", "LG", "LLG", "XL", "GR"] as const satisfies readonly AvatarSize[];

const toAvatarSize = (value: string | undefined): AvatarSize | undefined =>
  AVATAR_SIZES.find((avatarSize) => avatarSize === value);

// Type definitions for the props with `readonly` modifier
export interface UserNameTagComponentWithProviderProps {
  readonly id?: string | undefined;
  readonly handleDeleteIconClicked?: (() => void) | undefined;
  readonly showAvatarOnly?: boolean | undefined;
  readonly size?: string | undefined;
  readonly isCircle?: boolean | undefined;
  readonly isSquare?: boolean | undefined;
  readonly bgColor?: string | undefined;
  readonly isExternal?: boolean | undefined;
  readonly style?: React.CSSProperties | undefined;
  readonly maxAvatars?: number | undefined;
  readonly userIdsForGroup?: string[] | undefined;
  readonly fullName?: string | undefined;
  /** Email of an external (no-`id`) user — e.g. an email-sourced support ticket
   *  reporter. When set and there is no `id`, clicking the chip opens a profile
   *  modal showing the name + email. */
  readonly email?: string | undefined;
  readonly chipBackground?: string | undefined;
  readonly chipBorder?: string | undefined;
  readonly enableDetailsModalForGroupedUsers?: boolean | undefined;
  readonly detailsModalHeader?: TranslatedString | undefined;
}

function UserNameTagComponentWithProvider({
  id,
  handleDeleteIconClicked,
  showAvatarOnly = false,
  size,
  isCircle = true,
  isSquare = false,
  bgColor = "var(--system-colour-2)",
  isExternal = false,
  style,
  maxAvatars,
  userIdsForGroup = [],
  fullName,
  email,
  chipBackground,
  chipBorder,
  enableDetailsModalForGroupedUsers,
  detailsModalHeader,
}: UserNameTagComponentWithProviderProps) {
  const { theme } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const userTagData = useSelector((state: RootState) => state.userTagData); // Adjusted for state type

  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [showGroupedUserDetailsModal, setShowGroupedUserDetailsModal] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id && userFullName) {
      setShowUserProfileModal(true);
    } else if (!id && email) {
      // External (email-sourced) user with no account: show name + email in the modal.
      // `ExternalUserProfile` declares `fullName?: string`, so under `exactOptionalPropertyTypes`
      // the key has to be absent rather than present-and-undefined. The modal reads it as
      // `externalUser?.fullName`, so an absent key and an explicit `undefined` render the same.
      dispatch(openExternalUser({ ...(fullName !== undefined && { fullName }), email }));
    }
  };

  const handleGroupedAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowGroupedUserDetailsModal(true);
  };

  const onClose = () => {
    setShowUserProfileModal(false);
  };

  // `id` is this shared user-tag's loose `string` prop, passed from ~80 call sites (many of them
  // untyped grid rows), so this is the boundary where the account id enters the type system:
  // validate it instead of branding it, otherwise a grid row id would be fetched as an account.
  // Dropping the request for a non-ObjectId `id` costs nothing a user could see: the route
  // (`getUserDetailsByIdRequest`, packages/server/accounts/accounts.schema.ts) validates `userId`
  // with this same `objectIdString`, so such a call was already rejected server-side, `isLoaded`
  // never flipped, and the only trace left was a stuck `{ isLoading: true }` entry — exactly what
  // the "0" sentinel note in ListSpaces/listSpaces.tsx describes.
  const accountId = useMemo(() => {
    if (!id) return null;
    const parsed = objectIdString.safeParse(id);
    return parsed.success ? parsed.data : null;
  }, [id]);

  /**
   * `AvatarComponentProps` declares `fullName?: string` / `size?: AvatarSize` (cubeComponent/html/
   * AvatarComponent.tsx, outside this module), so under `exactOptionalPropertyTypes` those keys
   * have to be absent rather than present-and-undefined. React falls back to the component's own
   * default (`fullName = ""`, `size = "SM"`) for an absent prop and for an explicitly-undefined
   * one alike, so what renders is unchanged.
   */
  const avatarSize = toAvatarSize(size);

  const getGroupedAvatars = (ids: string[]) => {
    return ids.map((userId: string) => (
      <AvatarComponent
        key={userId}
        {...(fullName !== undefined && { fullName })}
        id={userId}
        {...(avatarSize !== undefined && { size: avatarSize })}
        isCircle={isCircle}
        bgColor={bgColor}
      />
    ));
  };

  let groupedAvatars;

  if (maxAvatars) {
    groupedAvatars = getGroupedAvatars(userIdsForGroup);
  }

  const isLoaded = id ? get(userTagData, [id, "isLoaded"]) : undefined;
  const userData = id ? userTagData[id]?.data : undefined;
  const profilePic = userData?.profilePic;
  const userFullName = userData?.fullName;
  const firstName = userData?.firstName;
  const lastName = userData?.lastName;

  function getInitials() {
    if (id && (firstName || lastName)) {
      // `getNameInitials` declares `{ firstName?: string; lastName?: string }`, so under
      // `exactOptionalPropertyTypes` an absent name means an absent key. It reads each member as
      // `userData?.firstName?.[0]`, so omitting a key and passing `undefined` produce the same
      // initials.
      return getNameInitials({
        ...(firstName !== undefined && { firstName }),
        ...(lastName !== undefined && { lastName }),
      });
    }
    return getNameInitials({
      firstName: fullName?.split(" ")[0] || "",
      lastName: fullName?.split(" ")[1] || "",
    });
  }

  return (
    <>
      {/* Mounted unconditionally: the tag below only renders once `isLoaded` flips, and this is
          what makes it flip. `AvatarComponent`'s own fetcher cannot stand in for it. */}
      {accountId !== null && <UserTagDataFetcher accountId={accountId} />}
      {(isLoaded || fullName) &&
        (showAvatarOnly ? (
          // Avatar-only rendering deliberately carries no grouping: `maxAvatars` and
          // `groupedAvatars` are left off rather than passed as an explicit `undefined`, which is
          // the same thing to `AvatarComponent` (neither has a default) and the only shape
          // `exactOptionalPropertyTypes` allows for an optional prop with no value. The remaining
          // spreads are the same note as `getGroupedAvatars` above.
          <AvatarComponent
            bgColor={bgColor}
            {...(avatarSize !== undefined && { size: avatarSize })}
            isCircle={isCircle}
            isSquare={isSquare}
            {...(id !== undefined && { id })}
            {...(fullName !== undefined && { fullName })}
            {...(style !== undefined && { style })}
          />
        ) : (
          <Chip
            label={id && userFullName ? userFullName : (fullName ?? "")}
            deleteIcon={<IconComponent name="cross" width="1rem" height="1rem" />}
            size="medium"
            avatar={
              id && profilePic ? (
                <Avatar src={profilePic} />
              ) : (
                <Avatar>
                  <div className="text-white">
                    {isExternal ? (
                      <div className="bg-orange-400 rounded-full w-8 h-8 flex items-center justify-center">
                        <IconComponent name="userProfileWhite" width="1.25rem" height="1.25rem" />
                      </div>
                    ) : (
                      getInitials()
                    )}
                  </div>
                </Avatar>
              )
            }
            onDelete={handleDeleteIconClicked}
            onClick={handleClick}
            sx={{
              background: chipBackground,
              border: chipBorder,
              color: `var(${theme.rootTextColor})`,
              lineHeight: "normal",
              ...(isExternal && {
                "&:hover": {
                  backgroundColor: chipBackground,
                },
              }),
            }}
          />
        ))}
      {showUserProfileModal && id && <UserProfileModal userId={id} onClose={onClose} />}

      {maxAvatars && (
        <Box onClick={enableDetailsModalForGroupedUsers ? handleGroupedAvatarClick : undefined}>
          {/* Same `exactOptionalPropertyTypes` note as `getGroupedAvatars` above: `style`,
              `size` and `groupedAvatars` are optional on `AvatarComponentProps`, so an absent
              value is spread away rather than handed over as an explicit `undefined`. */}
          <AvatarComponent
            {...(style !== undefined && { style })}
            bgColor={bgColor}
            {...(avatarSize !== undefined && { size: avatarSize })}
            isCircle={isCircle}
            isSquare={isSquare}
            maxAvatars={maxAvatars}
            {...(groupedAvatars !== undefined && { groupedAvatars })}
            userIdsForGroup={userIdsForGroup}
          />
        </Box>
      )}
      {showGroupedUserDetailsModal && userIdsForGroup.length && (
        <Suspense fallback={<></>}>
          {/* `GroupedUsersDetailsModalProps.detailsModalHeader` is `?: string`, so under
              `exactOptionalPropertyTypes` the prop is omitted rather than passed as `undefined`;
              the modal falls back to its own default header either way. */}
          <GroupedUsersDetailsModal
            {...(detailsModalHeader !== undefined && { detailsModalHeader })}
            ids={userIdsForGroup}
            closeModal={() => setShowGroupedUserDetailsModal(false)}
          />
        </Suspense>
      )}
    </>
  );
}

export default UserNameTagComponentWithProvider;
